import { cognitoUserManager, cognitoUserManagerSettings } from '@/configs/cognitoAuthConfig';
import { User } from 'oidc-client-ts';
import { type ReactNode, useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { AUTH_STATE, type LogoutOptions, LsStoreKey, type ScopeListedItem } from '@/types';
import { clearAppErrorSessionStorage } from '@/helpers/appErrorSession';
import { buildMessageCreatorDraftScopeId } from '@/helpers/messageCreatorDraftStorage';
import { beginLogoutWithDraftCheck, resolveMessageCreatorDraftUserId } from '@/helpers/messageCreatorLogoutGuard';
import { getDataFromLs } from '@/helpers/localStorage';
import { useChurchAdminCurrentScopes, useImpersonation, useLastSelectedChurch } from '@/hooks';
import AuthService from '@/services/auth.service';
import { impersonationService } from '@/services/impersonation.service';
import { impersonationSessionService } from '@/services/impersonationSession.service';
import UserManagerEventsService from '@/services/userManagerEvents.service';
import { AuthContext } from '@/providers/authContext';

const hasValidLocalAccessToken = (): boolean => {
  const accessToken = AuthService.getAccessToken();
  if (!accessToken) {
    return false;
  }

  return !AuthService.isTokenExpired();
};

export const AuthProvider = ({ children }: { children: ReactNode | ReactNode[] }) => {
  const auth = useAuth();
  const [, bumpAuthEvaluation] = useReducer((count: number) => count + 1, 0);
  const isSessionActiveForApi = auth.isAuthenticated || hasValidLocalAccessToken();
  const [localUserInfo, setLocalUserInfo] = useState<User | null>(null);
  const { isImpersonating } = useImpersonation();
  const { data: currentScope, isPlaceholderData: isScopesPlaceholderData } = useChurchAdminCurrentScopes({
    enabled: isSessionActiveForApi,
    filters: isImpersonating ? { limit: 500, page: 1 } : undefined,
  });
  const { initializeLastSelectedChurch } = useLastSelectedChurch();

  useEffect(() => {
    UserManagerEventsService.initialize(cognitoUserManager);
  }, []);

  useEffect(() => {
    if (!isSessionActiveForApi) {
      return;
    }

    if (!currentScope?.scopes?.length) {
      return;
    }

    // keepPreviousData can surface another session's scopes until the refetch completes.
    // Wait for fresh scopes before initializing church selection.
    if (isScopesPlaceholderData) {
      return;
    }

    initializeLastSelectedChurch(currentScope.scopes);
  }, [currentScope?.scopes, initializeLastSelectedChurch, isScopesPlaceholderData, isSessionActiveForApi]);

  const logout = useCallback(
    (options?: LogoutOptions): void => {
      const performLogout = async (): Promise<void> => {
        clearAppErrorSessionStorage();

        if (impersonationService.getSession().isImpersonating) {
          await impersonationSessionService.endSession();
          return;
        }

        AuthService.setLogoutIntent();
        // Capture the id_token before clearing local state — Cognito needs it
        // to identify and invalidate the correct SSO session on the server side.
        const currentUser = await cognitoUserManager.getUser();
        await AuthService.clearLocalAuthSession();
        impersonationService.clearSession();
        // Cognito's /logout expects client_id + logout_uri (not OIDC post_logout_redirect_uri).
        // oidc-client-ts only injects client_id when id_token_hint is absent, so we pass client_id
        // explicitly whenever we send id_token_hint — otherwise Cognito returns 400.
        const logoutReturnUri = import.meta.env.VITE_COGNITO_LOGOUT_URI;
        await cognitoUserManager.signoutRedirect({
          ...(currentUser?.id_token ? { id_token_hint: currentUser.id_token } : {}),
          client_id: cognitoUserManagerSettings.client_id,
          extraQueryParams: {
            logout_uri: logoutReturnUri,
          },
        });
      };

      if (options?.skipDraftCheck) {
        void performLogout();
        return;
      }

      const selectedChurch = getDataFromLs(LsStoreKey.LastSelectedChurch) as ScopeListedItem | undefined;

      const checkDraftAndLogout = async (): Promise<void> => {
        const currentUser = await cognitoUserManager.getUser();
        const accessToken = await AuthService.getValidAccessToken();
        const apiProfile = accessToken ? await AuthService.fetchUserProfileFromApi({ accessToken }) : null;
        const userId = resolveMessageCreatorDraftUserId({
          apiProfileSub: apiProfile?.sub,
          oidcUserSub: currentUser?.profile.sub || auth.user?.profile.sub,
          localUserSub: localUserInfo?.profile.sub,
        });
        const churchId = selectedChurch?.churchId || '';
        const scopeId = selectedChurch
          ? buildMessageCreatorDraftScopeId({
              churchId,
              allMembersGroupId: selectedChurch.allMembersGroupId,
              isGroupScope: selectedChurch.isGroupScope,
            })
          : '';

        await beginLogoutWithDraftCheck({
          userId,
          churchId,
          scopeId,
          isImpersonating,
          performLogout: () => {
            void performLogout();
          },
        });
      };

      void checkDraftAndLogout();
    },
    [auth.user?.profile.sub, isImpersonating, localUserInfo?.profile.sub]
  );

  const getAuthState = (): AUTH_STATE => {
    if (hasValidLocalAccessToken()) return AUTH_STATE.AUTHENTICATED;
    if (auth.isLoading) return AUTH_STATE.LOADING;
    if (auth.isAuthenticated) return AUTH_STATE.AUTHENTICATED;
    return AUTH_STATE.UNAUTHENTICATED;
  };
  const state = getAuthState();

  useEffect(() => {
    if (state !== AUTH_STATE.AUTHENTICATED) {
      setLocalUserInfo(null);
      return;
    }

    if (auth.user) {
      setLocalUserInfo(null);
      return;
    }

    const accessToken = AuthService.getAccessToken();
    if (!accessToken) {
      setLocalUserInfo(null);
      return;
    }

    const expiresAtMs = AuthService.getAccessTokenExpiresAtMs();
    const expiresAtSeconds = expiresAtMs ? Math.floor(expiresAtMs / 1000) : undefined;

    const hydrate = async (): Promise<void> => {
      const profileFromJwt = AuthService.getUserProfileFromAccessToken(accessToken);

      const hasBasicName =
        Boolean(profileFromJwt?.given_name?.trim()) ||
        Boolean(profileFromJwt?.family_name?.trim()) ||
        Boolean(profileFromJwt?.preferred_username?.trim());

      const profile =
        hasBasicName && profileFromJwt
          ? profileFromJwt
          : (await AuthService.fetchUserProfileFromApi({ accessToken })) ||
            (await AuthService.fetchUserProfileFromCognito({ accessToken })) ||
            profileFromJwt;

      if (!profile) {
        setLocalUserInfo(null);
        return;
      }

      const user = new User({
        access_token: accessToken,
        token_type: 'Bearer',
        profile,
        ...(typeof expiresAtSeconds === 'number' ? { expires_at: expiresAtSeconds } : {}),
      });

      setLocalUserInfo(user);
    };

    void hydrate();
  }, [auth.user, state]);

  const userInfo = useMemo(() => auth.user ?? localUserInfo ?? null, [auth.user, localUserInfo]);

  return (
    <AuthContext.Provider
      value={{
        state,
        logout,
        userManager: cognitoUserManager,
        setAuthenticated: () => {},
        userInfo,
        setUserInfo: () => {},
        bumpAuthEvaluation,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
